import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
} from 'react';

/** A rendered video or graphic, the panel's original tenants. */
export interface OpenMediaArtifact {
  kind: 'video' | 'graphic';
  id: string;
  /**
   * The content item this asset belongs to, when the opener knows it.
   *
   * The panel's Save / Schedule / Reject act on the ITEM — the decision is
   * about the post, not the file — so without this the panel can show content
   * but not decide on it. Optional because a caller may only have an asset id;
   * the actions are simply absent there rather than guessed at.
   */
  itemId?: string;
}

/**
 * A saved Meta ad: a creative (a video, a graphic or an uploaded asset) plus
 * the copy around it. Keyed by the AD id, not its creative's, because one
 * graphic can back several ads and the panel shows the ad — name, copy,
 * status — not just the picture. An ad belongs to no content item, so it has
 * no versions to select and no Save / Schedule / Reject.
 */
export interface OpenAdArtifact {
  kind: 'ad';
  id: string;
  itemId?: undefined;
  /**
   * The graphic or video this ad is showing, when the opener knows it. The
   * card that is rendering that creative reads it to decide whether an open
   * ad is WAITING ON IT (then the picture lands in the ad's frame and the
   * card stays quiet) or is unrelated (then the card opens itself as usual).
   */
  creativeId?: string;
}

/**
 * A discriminated union rather than one interface with a `kind` field, so a
 * `kind` check narrows the whole object: the media panel takes only media,
 * and the compiler, not a cast, says an ad never reaches it.
 */
export type OpenArtifact = OpenMediaArtifact | OpenAdArtifact;

interface ArtifactPanelValue {
  /** What the panel is showing, or null when it is closed. */
  artifact: OpenArtifact | null;
  /** The owner asked for it: always opens, and forgives an earlier dismissal. */
  openArtifact: (artifact: OpenArtifact) => void;
  /**
   * A card opening on its own because a render landed. Ignored for an
   * artifact the owner has CLOSED since it was last open — rejecting or
   * dismissing a render still in progress must not have it spring back when
   * the worker finishes.
   */
  autoOpenArtifact: (artifact: OpenArtifact) => void;
  close: () => void;
}

const keyOf = (artifact: OpenArtifact) => `${artifact.kind}:${artifact.id}`;

const ArtifactPanelContext = createContext<ArtifactPanelValue | null>(null);

/**
 * The side panel a finished render opens into.
 *
 * WHY A CONTEXT rather than state on the chat view. The thing that knows a
 * render has finished is a card buried several levels down in a message list,
 * and the thing that has to react is the page layout. Threading a callback
 * from the route through `MessageList` → `ToolRenderer` → the card would put a
 * prop about panel layout on every component in between, none of which have
 * any business knowing the panel exists.
 *
 * It holds a KIND and an ID and nothing else. The panel fetches its own state,
 * so a card that starts a render hands over an identity and forgets about it —
 * which matters because the render outlives the turn that started it, and
 * often the card too.
 *
 * `kind` is carried rather than inferred from the id: a video, a graphic and an
 * ad are different endpoints and different players, and asking the panel to
 * guess which it has from a uuid would be a lookup with a wrong answer
 * available.
 */
export function ArtifactPanelProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const [artifact, setArtifact] = useState<OpenArtifact | null>(null);
  // Artifacts the owner closed. A ref, not state: it is consulted, never
  // rendered, and growing it must not re-render every card in the chat.
  const dismissed = useRef(new Set<string>());

  const openArtifact = useCallback((next: OpenArtifact) => {
    dismissed.current.delete(keyOf(next));
    setArtifact(next);
  }, []);
  const autoOpenArtifact = useCallback((next: OpenArtifact) => {
    if (dismissed.current.has(keyOf(next))) return;
    setArtifact(next);
  }, []);
  const close = useCallback(() => {
    setArtifact((current) => {
      if (current) dismissed.current.add(keyOf(current));
      return null;
    });
  }, []);

  const value = useMemo(
    () => ({ artifact, openArtifact, autoOpenArtifact, close }),
    [artifact, openArtifact, autoOpenArtifact, close]
  );

  return (
    <ArtifactPanelContext.Provider value={value}>
      {children}
    </ArtifactPanelContext.Provider>
  );
}

/**
 * Open the side panel on a finished render.
 *
 * Returns a no-op outside a provider ON PURPOSE. The same cards render in the
 * batch review workspace and in the compact mobile sheet, neither of which has
 * a side panel — and a card that threw because of where it was mounted would
 * be a worse failure than one that quietly does not open a panel that does not
 * exist.
 */
export function useHasArtifactPanel(): boolean {
  return useContext(ArtifactPanelContext) !== null;
}

/**
 * Whether a provider already exists above this point.
 *
 * The chat mounts its own provider so it works standalone, but the review page
 * needs one ABOVE both the chat and the panel — its queue opens artifacts, and
 * a provider nested inside the chat would be invisible to it. Two providers
 * would leave the queue writing to one and the panel reading the other.
 */
export function useArtifactPanel(): ArtifactPanelValue {
  const ctx = useContext(ArtifactPanelContext);
  return (
    ctx ?? {
      artifact: null,
      openArtifact: () => undefined,
      autoOpenArtifact: () => undefined,
      close: () => undefined,
    }
  );
}
